import { pageBgColor } from "@/utils/theme";
import { HelmetDexteel, getMomentTz } from "@dexteel/mesf-core";
import { DashboardOutlined, TableChartOutlined } from "@mui/icons-material";
import {
	Box,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
} from "@mui/material";
import {
	parseAsArrayOf,
	parseAsString,
	parseAsStringLiteral,
	useQueryState,
	useQueryStates,
} from "nuqs";
import { useEffect, useState } from "react";
import { UtilityMessage } from "../shared/components/UtilityStates";
import { UtilityTabs } from "../shared/components/UtilityTabs";
import { buildConsumptionChartModel } from "../shared/consumption/components/consumption-chart/chartModel";
import { ConsumptionControls } from "../shared/consumption/components/ConsumptionControls";
import { ConsumptionFilterBar } from "../shared/consumption/components/ConsumptionFilterBar";
import { ConsumptionReport } from "../shared/consumption/components/ConsumptionReport";
import {
	ConsumptionError,
	ConsumptionSkeletons,
} from "../shared/consumption/components/ConsumptionStates";
import { useUtilityConsumption } from "../shared/consumption/hooks/useUtilityConsumption";
import { useUtilityConsumptionMeters } from "../shared/consumption/hooks/useUtilityConsumptionMeters";
import {
	UTILITY_CONSUMPTION_CHART_METRIC,
	UTILITY_CONSUMPTION_MODE,
	UTILITY_CONSUMPTION_VIEW,
	type UtilityConsumptionMode,
	type UtilityConsumptionReportRange,
	type UtilityConsumptionView,
} from "../shared/consumption/models/UtilityConsumption";
import {
	fromUtilityConsumptionDateKey,
	getCurrentUtilityConsumptionPeriod,
	getDefaultUtilityConsumptionReportRange,
	getUtilityConsumptionPeriod,
	toUtilityConsumptionDateKey,
} from "../shared/consumption/utils/calendar";
import { parseAsUtilityConsumptionMode } from "../shared/consumption/utils/queryParsers";
import { CONSUMPTION_CAPTION_SX } from "../shared/consumption/utils/tokens";
import { useUtilityKind } from "../shared/hooks/useUtilityKind";
import { UTILITY_KIND } from "../shared/models/Utility";
import { EngineeringHighlights } from "./components/EngineeringHighlights";
import { MeterStatusTable } from "./components/MeterStatusTable";
import { PeriodConsumptionPanel } from "./components/PeriodConsumptionPanel";

const moment = getMomentTz();

const ENGINEERING_UTILITIES = [UTILITY_KIND.WATER];

const ENGINEERING_QUERY = {
	view: parseAsStringLiteral(
		Object.values(UTILITY_CONSUMPTION_VIEW),
	).withDefault(UTILITY_CONSUMPTION_VIEW.DASHBOARD),
	chart: parseAsUtilityConsumptionMode.withDefault(
		UTILITY_CONSUMPTION_MODE.DAY,
	),
	date: parseAsString,
	from: parseAsString,
	to: parseAsString,
};

const formatRelativeTime = (value: Date, now: Date) => {
	const elapsedMinutes = Math.floor((now.getTime() - value.getTime()) / 60_000);
	if (elapsedMinutes < 1) return "just now";
	if (elapsedMinutes < 60) return `${elapsedMinutes} min ago`;
	if (elapsedMinutes < 1440) return `${Math.floor(elapsedMinutes / 60)} h ago`;
	return `${Math.floor(elapsedMinutes / 1440)} d ago`;
};

export const UtilityEngineeringPage = () => {
	const [meterIds, setMeterIds] = useQueryState(
		"meters",
		parseAsArrayOf(parseAsString),
	);
	const [{ view, chart: mode, date, from, to }, setQuery] =
		useQueryStates(ENGINEERING_QUERY);

	const [utility, setUtility] = useUtilityKind(ENGINEERING_UTILITIES);
	const referenceDate = fromUtilityConsumptionDateKey(
		date,
		() => getCurrentUtilityConsumptionPeriod(mode).startUtc,
	);

	const [hoveredMeterId, setHoveredMeterId] = useState<string | null>(null);

	const period = getUtilityConsumptionPeriod(mode, referenceDate);
	const defaultReportRange = getDefaultUtilityConsumptionReportRange();
	const reportRange = {
		startDateKey: from ?? defaultReportRange.startDateKey,
		endDateKey: to ?? defaultReportRange.endDateKey,
	};
	const setReportRange = (range: UtilityConsumptionReportRange) =>
		setQuery({ from: range.startDateKey, to: range.endDateKey });
	const setView = (nextView: UtilityConsumptionView) =>
		setQuery({ view: nextView });

	const {
		data: meters,
		error: meterError,
		isError: isMeterError,
		isLoading: isMeterLoading,
		refetch: refetchMeters,
		resolvedExternalMeterIds,
		isEmptySelection,
	} = useUtilityConsumptionMeters({
		targetId: null,
		externalMeterIds: meterIds,
	});

	const { data, error, isError, isLoading, refetch } = useUtilityConsumption({
		targetId: null,
		enabled: view === UTILITY_CONSUMPTION_VIEW.DASHBOARD,
		utility,
		externalMeterIds: resolvedExternalMeterIds,
		mode,
		period,
		waitForEveryMeter: false,
	});

	const chartModel =
		data === undefined
			? null
			: buildConsumptionChartModel({
					buckets: isEmptySelection ? [] : data.mainBuckets,
					meters: meters ?? [],
					mode,
					metric: UTILITY_CONSUMPTION_CHART_METRIC.GALLONS,
				});

	const summary =
		data === undefined
			? undefined
			: isEmptySelection
				? {
						...data.summary,
						Last7Gallons: null,
						SevenDayChangePercent: null,
						ThirtyDayAverageGallons: null,
						MonthToDateGallons: null,
						LastMonthTotalGallons: null,
						YearToDateGallons: null,
						MainTotalGallons: null,
						IsMainPartial: false,
					}
				: data.summary;

	const setPeriodMode = (nextMode: UtilityConsumptionMode) =>
		setQuery({ chart: nextMode, date: null });
	const setReferenceDate = (value: Date) =>
		setQuery({ date: toUtilityConsumptionDateKey(value) });
	const filterByMeter = (meterId: string) =>
		setMeterIds((current) =>
			current !== null && current.length === 1 && current[0] === meterId
				? null
				: [meterId],
		);

	const dataThroughUtc = data?.summary.DataThroughUtc ?? null;
	const dataThrough = dataThroughUtc
		? `Data through ${moment(dataThroughUtc).format(
				"MMM D, YYYY HH:mm",
			)} · ${formatRelativeTime(dataThroughUtc, new Date())}`
		: null;

	useEffect(() => {
		if (meterIds === null || meters === undefined) return;
		const configuredIds = new Set(meters.map((meter) => meter.ExternalMeterId));
		const validIds = meterIds.filter((meterId) => configuredIds.has(meterId));
		if (validIds.length === meterIds.length) return;
		setMeterIds(validIds.length === 0 ? null : validIds);
	}, [meterIds, meters]);

	return (
		<>
			<HelmetDexteel title="Engineering Report" />
			<Box
				sx={{
					bgcolor: pageBgColor,
					boxSizing: "border-box",
					display: "flex",
					flexDirection: "column",
					height: { lg: "calc(100vh - 90px)" },
					minHeight: { lg: "calc(100vh - 90px)" },
					p: { xs: 1, md: 2 },
				}}
			>
				<Box
					sx={{
						display: "flex",
						flexWrap: "wrap",
						justifyContent: "space-between",
						alignItems: "center",
						gap: 1,
						mb: 1,
					}}
				>
					<Typography
						component="h1"
						sx={{ fontSize: "1.5rem", fontWeight: 600, lineHeight: 1.25 }}
					>
						Engineering Report
					</Typography>
					<Box
						sx={{
							display: "flex",
							gap: 0.75,
							alignItems: "center",
							flexWrap: "wrap",
						}}
					>
						{dataThrough && (
							<Typography component="div" sx={CONSUMPTION_CAPTION_SX}>
								{dataThrough}
							</Typography>
						)}
						<ToggleButtonGroup
							exclusive
							size="small"
							value={view}
							aria-label="Consumption view"
							onChange={(_, value: UtilityConsumptionView | null) =>
								value && setView(value)
							}
							sx={{
								"& .MuiToggleButton-root": { px: 1.25, textTransform: "none" },
								"& .Mui-selected": {
									bgcolor: "primary.main",
									color: "primary.contrastText",
									"&:hover": { bgcolor: "primary.dark" },
								},
							}}
						>
							<Tooltip title="Dashboard view: cards and charts">
								<ToggleButton
									value={UTILITY_CONSUMPTION_VIEW.DASHBOARD}
									aria-label="Dashboard view"
								>
									<DashboardOutlined fontSize="small" sx={{ mr: 0.5 }} />
									Dashboard
								</ToggleButton>
							</Tooltip>
							<Tooltip title="Report view: detailed table">
								<ToggleButton
									value={UTILITY_CONSUMPTION_VIEW.REPORT}
									aria-label="Report view"
								>
									<TableChartOutlined fontSize="small" sx={{ mr: 0.5 }} />
									Report
								</ToggleButton>
							</Tooltip>
						</ToggleButtonGroup>
					</Box>
				</Box>
				<UtilityTabs
					utility={utility}
					available={ENGINEERING_UTILITIES}
					onUtilityChange={setUtility}
				/>
				{isMeterError ? (
					<ConsumptionError
						message={
							meterError instanceof Error
								? meterError.message
								: "Unexpected response."
						}
						onRetry={() => refetchMeters()}
					/>
				) : view === UTILITY_CONSUMPTION_VIEW.DASHBOARD ? (
					<>
						<ConsumptionFilterBar
							meters={meters ?? []}
							isMeterLoading={isMeterLoading}
							selectedMeterIds={meterIds}
							onMeterChange={setMeterIds}
						/>
						<Box
							sx={{
								display: "flex",
								flex: 1,
								flexDirection: "column",
								gap: 1,
								minHeight: 0,
							}}
						>
							{isError && (
								<ConsumptionError
									message={
										error instanceof Error
											? error.message
											: "Unexpected response."
									}
									onRetry={() => refetch()}
								/>
							)}
							{isLoading && data === undefined && <ConsumptionSkeletons />}
							{data && meters?.length === 0 && (
								<UtilityMessage
									title="No meters configured"
									detail="No active water meters are assigned to the plant."
								/>
							)}
							{data &&
								(meters?.length ?? 0) > 0 &&
								!data.summary.IsAvailable && (
									<UtilityMessage
										title={
											meterIds !== null
												? "Selected meters have no readings"
												: "No reporting meters"
										}
									/>
								)}
							{data?.summary.IsAvailable &&
								summary !== undefined &&
								chartModel !== null && (
									<>
										<EngineeringHighlights summary={summary} />
										<PeriodConsumptionPanel
											model={chartModel}
											highlightedMeterId={hoveredMeterId}
											summary={summary}
											period={period}
											onMeterFilter={filterByMeter}
											controls={
												<ConsumptionControls
													mode={mode}
													referenceDate={period.startUtc}
													onModeChange={setPeriodMode}
													onReferenceDateChange={setReferenceDate}
												/>
											}
											table={
												<MeterStatusTable
													onMeterChange={setMeterIds}
													onMeterHover={setHoveredMeterId}
													meters={meters ?? []}
													statuses={data.meterStatuses}
													selectedMeterIds={
														isEmptySelection ? [] : resolvedExternalMeterIds
													}
												/>
											}
										/>
									</>
								)}
						</Box>
					</>
				) : (
					<ConsumptionReport
						targetId={null}
						utility={utility}
						meters={meters ?? []}
						isMeterLoading={isMeterLoading}
						selectedMeterIds={meterIds}
						resolvedMeterIds={resolvedExternalMeterIds}
						areMetersLoaded={meters !== undefined}
						isEmptySelection={isEmptySelection}
						range={reportRange}
						showCost={false}
						showRatio={false}
						showSection={false}
						onMeterChange={setMeterIds}
						onRangeChange={setReportRange}
					/>
				)}
			</Box>
		</>
	);
};
